import { useState } from 'react';
import {
  Zap,
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Plus,
  Search,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { API_BASE } from '@/lib/api';
import { Button } from '@/components/ui/button';

// ============================================================================
// TYPES
// ============================================================================

interface OddsEvent {
  id: string;
  homeTeam: string;
  awayTeam: string;
  commenceTime: string;
}

interface PlayerPropRow {
  id: string;
  playerId: string;
  playerName: string;
  gameId: string | null;
  gameTime: string;
  season: number;
  week: number;
  category: string;
  line: number;
  overOdds: number;
  underOdds: number;
  isActive: boolean;
  result: string;
}

interface SyncResult {
  synced: number;
  message: string;
  usage?: { requestsUsed: number; remaining: number };
}

interface UsageInfo {
  configured: boolean;
  usage: {
    month: string;
    requestCount: number;
    remainingRequests: number;
    monthlyLimit: number;
    isNearLimit: boolean;
  };
}

// ============================================================================
// HELPERS
// ============================================================================

const CATEGORY_LABELS: Record<string, string> = {
  POINTS: 'Points',
  REBOUNDS: 'Rebounds',
  ASSISTS: 'Assists',
  STEALS: 'Steals',
  BLOCKS: 'Blocks',
  THREE_POINTERS_MADE: '3PM',
  TURNOVERS: 'Turnovers',
  POINTS_REBOUNDS_ASSISTS: 'PRA',
  DOUBLE_DOUBLE: 'Dbl-Dbl',
  POINTS_REBOUNDS: 'Pts+Reb',
  POINTS_ASSISTS: 'Pts+Ast',
  REBOUNDS_ASSISTS: 'Reb+Ast',
};

function formatOdds(odds: number): string {
  return odds >= 0 ? `+${odds}` : `${odds}`;
}

function getDefaultDates() {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const daysUntilMonday = dayOfWeek === 0 ? 1 : 8 - dayOfWeek;

  const startDate = new Date(now);
  startDate.setDate(now.getDate() + daysUntilMonday);
  startDate.setHours(19, 0, 0, 0);

  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + 6);
  endDate.setHours(23, 59, 0, 0);

  return {
    startTime: startDate.toISOString().slice(0, 16),
    endTime: endDate.toISOString().slice(0, 16),
  };
}

// ============================================================================
// ALT-LINE SLIDER COMPONENT
// ============================================================================

function AltLineSlider({
  category,
  lines,
}: {
  category: string;
  lines: PlayerPropRow[];
}) {
  const sorted = [...lines].sort((a, b) => a.line - b.line);
  const [sliderIndex, setSliderIndex] = useState(() => {
    // Default to the middle line (closest to primary/median)
    return Math.floor(sorted.length / 2);
  });

  const current = sorted[sliderIndex];
  if (!current) return null;

  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  const hasAlts = sorted.length > 1;

  // Calculate implied probability from American odds
  const impliedProb = (odds: number) => {
    if (odds > 0) return 100 / (odds + 100);
    return Math.abs(odds) / (Math.abs(odds) + 100);
  };

  const overProb = impliedProb(current.overOdds);
  const underProb = impliedProb(current.underOdds);

  return (
    <div className="py-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-zinc-400 uppercase tracking-wider">
          {CATEGORY_LABELS[category] || category}
        </span>
        {hasAlts && (
          <span className="text-[10px] text-zinc-600">
            {sorted.length} lines: {min.line} — {max.line}
          </span>
        )}
      </div>

      {/* Current line display */}
      <div className="flex items-center gap-4 mb-2">
        <div className="flex-1 flex items-center justify-between bg-zinc-800/80 rounded-lg px-4 py-2.5">
          {/* Over side */}
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-green-400" />
            <div>
              <span className="text-green-400 font-mono font-semibold text-sm">
                {formatOdds(current.overOdds)}
              </span>
              <span className="text-zinc-600 text-[10px] ml-1.5">
                {(overProb * 100).toFixed(0)}%
              </span>
            </div>
          </div>

          {/* Line value */}
          <div className="text-center">
            <div className="text-2xl font-bold text-white font-mono">{current.line}</div>
          </div>

          {/* Under side */}
          <div className="flex items-center gap-2">
            <div className="text-right">
              <span className="text-red-400 font-mono font-semibold text-sm">
                {formatOdds(current.underOdds)}
              </span>
              <span className="text-zinc-600 text-[10px] ml-1.5">
                {(underProb * 100).toFixed(0)}%
              </span>
            </div>
            <TrendingDown className="h-4 w-4 text-red-400" />
          </div>
        </div>
      </div>

      {/* Slider */}
      {hasAlts && (
        <div className="px-1">
          <input
            type="range"
            min={0}
            max={sorted.length - 1}
            value={sliderIndex}
            onChange={(e) => setSliderIndex(Number(e.target.value))}
            className="w-full h-1.5 rounded-full appearance-none cursor-pointer
              bg-gradient-to-r from-green-500/40 via-zinc-600 to-red-500/40
              [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
              [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-md
              [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-zinc-400
              [&::-webkit-slider-thumb]:hover:border-amber-400 [&::-webkit-slider-thumb]:transition-colors
              [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full
              [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-zinc-400"
          />
          <div className="flex justify-between mt-0.5">
            <span className="text-[10px] text-green-500/60 font-mono">{min.line}</span>
            <span className="text-[10px] text-red-500/60 font-mono">{max.line}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function PlayerPropSliders({
  playerName,
  props: playerProps,
}: {
  playerName: string;
  props: PlayerPropRow[];
}) {
  // Group by category
  const byCategory = playerProps.reduce<Record<string, PlayerPropRow[]>>((acc, p) => {
    if (!acc[p.category]) acc[p.category] = [];
    acc[p.category].push(p);
    return acc;
  }, {});

  // Count total alt lines (categories with >1 line)
  const altCount = Object.values(byCategory).reduce(
    (sum, lines) => sum + (lines.length > 1 ? lines.length - 1 : 0),
    0
  );

  return (
    <div className="border border-zinc-800 rounded-lg overflow-hidden">
      <div className="px-4 py-2 bg-zinc-800/50 flex items-center justify-between">
        <span className="font-medium text-white">{playerName}</span>
        <div className="flex items-center gap-2">
          {altCount > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/15 text-amber-400 rounded font-medium">
              {altCount} alt{altCount !== 1 ? 's' : ''}
            </span>
          )}
          <span className="text-xs text-zinc-500">
            {Object.keys(byCategory).length} market{Object.keys(byCategory).length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>
      <div className="px-4 divide-y divide-zinc-800/50">
        {Object.entries(byCategory).map(([cat, lines]) => (
          <AltLineSlider key={cat} category={cat} lines={lines} />
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function AdminPropBoostPage() {
  const { accessToken } = useAuthStore();
  const defaultDates = getDefaultDates();

  // Section collapse state
  const [showCreate, setShowCreate] = useState(true);
  const [showSync, setShowSync] = useState(true);
  const [showProps, setShowProps] = useState(true);

  // Create event state
  const [eventForm, setEventForm] = useState({
    name: 'PropBoost Test Event',
    season: 2025,
    week: 1,
    startTime: defaultDates.startTime,
    endTime: defaultDates.endTime,
    entryFee: 100,
    prizePool: 10000,
    maxProps: 7,
    propCostCoins: 50,
  });
  const [creating, setCreating] = useState(false);
  const [createResult, setCreateResult] = useState<{ id: string; name: string } | null>(null);
  const [createError, setCreateError] = useState('');

  // Sync state
  const [syncSeason, setSyncSeason] = useState(2025);
  const [syncWeek, setSyncWeek] = useState(1);
  const [includeAlts, setIncludeAlts] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [syncError, setSyncError] = useState('');

  // API usage
  const [usage, setUsage] = useState<UsageInfo | null>(null);
  const [loadingUsage, setLoadingUsage] = useState(false);

  // Odds events preview
  const [oddsEvents, setOddsEvents] = useState<OddsEvent[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [eventsError, setEventsError] = useState('');

  // Props viewer
  const [props, setProps] = useState<PlayerPropRow[]>([]);
  const [propsTotal, setPropsTotal] = useState(0);
  const [loadingProps, setLoadingProps] = useState(false);
  const [propsError, setPropsError] = useState('');
  const [propsFilter, setPropsFilter] = useState({
    season: 2025,
    week: 1,
    category: '',
    playerName: '',
  });

  // ── API Helpers ──────────────────────────────────────────────────────────

  const headers = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  });

  const createEvent = async () => {
    setCreating(true);
    setCreateError('');
    setCreateResult(null);

    try {
      const res = await fetch(`${API_BASE}/api/admin/events`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          name: eventForm.name,
          sport: 'NBA',
          category: 'WEEKLY',
          format: 'PROP_BOOST',
          tier: 'STANDARD',
          status: 'OPEN',
          season: eventForm.season,
          week: eventForm.week,
          entryFee: eventForm.entryFee,
          entryFeeType: 'COINS',
          prizePool: eventForm.prizePool,
          prizeType: 'COINS',
          startTime: new Date(eventForm.startTime).toISOString(),
          endTime: new Date(eventForm.endTime).toISOString(),
          rarityGate: 'OPEN',
          requiresCards: true,
          maxCardsPerEntry: 8,
          cardIntegration: 'REQUIRED',
          settings: {
            maxProps: eventForm.maxProps,
            propCostCoins: eventForm.propCostCoins,
            rosterSize: 8,
            scoringFormat: 'STANDARD',
          },
        }),
      });

      const data = await res.json();
      if (res.ok) {
        setCreateResult({ id: data.event.id, name: data.event.name });
      } else {
        setCreateError(data.error || 'Failed to create event');
      }
    } catch {
      setCreateError('Failed to create event');
    } finally {
      setCreating(false);
    }
  };

  const fetchUsage = async () => {
    setLoadingUsage(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/props/sync`, {
        headers: headers(),
      });
      if (res.ok) {
        const data = await res.json();
        setUsage(data);
      }
    } catch {
      // ignore
    } finally {
      setLoadingUsage(false);
    }
  };

  const fetchOddsEvents = async () => {
    setLoadingEvents(true);
    setEventsError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/props/events`, {
        headers: headers(),
      });
      const data = await res.json();
      if (res.ok) {
        setOddsEvents(data.events);
      } else {
        setEventsError(data.error || 'Failed to fetch events');
      }
    } catch {
      setEventsError('Failed to fetch events');
    } finally {
      setLoadingEvents(false);
    }
  };

  const syncProps = async () => {
    setSyncing(true);
    setSyncError('');
    setSyncResult(null);

    try {
      const res = await fetch(`${API_BASE}/api/admin/props/sync`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          season: syncSeason,
          week: syncWeek,
          includeAlts,
        }),
      });

      const data = await res.json();
      if (res.ok) {
        setSyncResult(data);
        // Auto-refresh props viewer
        fetchProps();
      } else {
        setSyncError(data.error || 'Failed to sync props');
      }
    } catch {
      setSyncError('Failed to sync props');
    } finally {
      setSyncing(false);
    }
  };

  const fetchProps = async () => {
    setLoadingProps(true);
    setPropsError('');

    try {
      const params = new URLSearchParams();
      params.set('season', String(propsFilter.season));
      params.set('week', String(propsFilter.week));
      if (propsFilter.category) params.set('category', propsFilter.category);
      if (propsFilter.playerName) params.set('playerName', propsFilter.playerName);
      params.set('limit', '200');

      const res = await fetch(`${API_BASE}/api/admin/props?${params}`, {
        headers: headers(),
      });

      const data = await res.json();
      if (res.ok) {
        setProps(data.props);
        setPropsTotal(data.total);
      } else {
        setPropsError(data.error || 'Failed to fetch props');
      }
    } catch {
      setPropsError('Failed to fetch props');
    } finally {
      setLoadingProps(false);
    }
  };

  // Group props by player for display
  const groupedProps = props.reduce<Record<string, PlayerPropRow[]>>((acc, prop) => {
    if (!acc[prop.playerName]) acc[prop.playerName] = [];
    acc[prop.playerName].push(prop);
    return acc;
  }, {});

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="h-8 w-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
          <Zap className="h-4 w-4 text-amber-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">PropBoost Test</h1>
          <p className="text-sm text-zinc-400">Create events, sync props from Odds API, view results</p>
        </div>
      </div>

      {/* ── Section 1: Create PropBoost Event ─────────────────────────────── */}
      <div className="bg-zinc-900 rounded-xl border border-zinc-800">
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="w-full flex items-center justify-between p-4 text-left"
        >
          <div className="flex items-center gap-2">
            <Plus className="h-4 w-4 text-green-400" />
            <h2 className="text-lg font-semibold text-white">Create PropBoost Event</h2>
          </div>
          {showCreate ? (
            <ChevronUp className="h-4 w-4 text-zinc-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-zinc-400" />
          )}
        </button>

        {showCreate && (
          <div className="px-4 pb-4 space-y-4 border-t border-zinc-800 pt-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-zinc-400 mb-1">Event Name</label>
                <input
                  type="text"
                  value={eventForm.name}
                  onChange={(e) => setEventForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-amber-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">Season</label>
                <input
                  type="number"
                  value={eventForm.season}
                  onChange={(e) => setEventForm((f) => ({ ...f, season: Number(e.target.value) }))}
                  className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-amber-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">Week</label>
                <input
                  type="number"
                  value={eventForm.week}
                  onChange={(e) => setEventForm((f) => ({ ...f, week: Number(e.target.value) }))}
                  className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-amber-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">Entry Fee (coins)</label>
                <input
                  type="number"
                  value={eventForm.entryFee}
                  onChange={(e) => setEventForm((f) => ({ ...f, entryFee: Number(e.target.value) }))}
                  className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-amber-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">Start Time</label>
                <input
                  type="datetime-local"
                  value={eventForm.startTime}
                  onChange={(e) => setEventForm((f) => ({ ...f, startTime: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-amber-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">End Time</label>
                <input
                  type="datetime-local"
                  value={eventForm.endTime}
                  onChange={(e) => setEventForm((f) => ({ ...f, endTime: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-amber-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">Max Props / Entry</label>
                <input
                  type="number"
                  value={eventForm.maxProps}
                  onChange={(e) => setEventForm((f) => ({ ...f, maxProps: Number(e.target.value) }))}
                  className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-amber-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">Prop Cost (coins)</label>
                <input
                  type="number"
                  value={eventForm.propCostCoins}
                  onChange={(e) => setEventForm((f) => ({ ...f, propCostCoins: Number(e.target.value) }))}
                  className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-amber-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">Prize Pool</label>
                <input
                  type="number"
                  value={eventForm.prizePool}
                  onChange={(e) => setEventForm((f) => ({ ...f, prizePool: Number(e.target.value) }))}
                  className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-amber-500 focus:outline-none"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button onClick={createEvent} disabled={creating}>
                {creating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4" />
                    Create Event
                  </>
                )}
              </Button>
            </div>

            {createResult && (
              <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400 text-sm">
                <CheckCircle className="h-4 w-4 flex-shrink-0" />
                <span>
                  Created: <strong>{createResult.name}</strong> (ID: {createResult.id})
                </span>
              </div>
            )}

            {createError && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span>{createError}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Section 2: Sync Props ──────────────────────────────────────────── */}
      <div className="bg-zinc-900 rounded-xl border border-zinc-800">
        <button
          onClick={() => setShowSync(!showSync)}
          className="w-full flex items-center justify-between p-4 text-left"
        >
          <div className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4 text-blue-400" />
            <h2 className="text-lg font-semibold text-white">Sync Props from Odds API</h2>
          </div>
          {showSync ? (
            <ChevronUp className="h-4 w-4 text-zinc-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-zinc-400" />
          )}
        </button>

        {showSync && (
          <div className="px-4 pb-4 space-y-4 border-t border-zinc-800 pt-4">
            {/* API Usage */}
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm" onClick={fetchUsage} disabled={loadingUsage}>
                {loadingUsage ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Check API Usage'}
              </Button>
              {usage && (
                <span className={cn(
                  'text-sm',
                  usage.usage.isNearLimit ? 'text-red-400' : 'text-zinc-400'
                )}>
                  {usage.usage.requestCount} / {usage.usage.monthlyLimit} requests used
                  ({usage.usage.remainingRequests} remaining)
                  {!usage.configured && ' — API key not configured!'}
                </span>
              )}
            </div>

            {/* Preview Odds Events */}
            <div>
              <Button variant="outline" size="sm" onClick={fetchOddsEvents} disabled={loadingEvents}>
                {loadingEvents ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Preview NBA Games'}
              </Button>
              {eventsError && (
                <p className="text-red-400 text-sm mt-2">{eventsError}</p>
              )}
              {oddsEvents.length > 0 && (
                <div className="mt-3 space-y-1">
                  <p className="text-xs text-zinc-500 mb-2">{oddsEvents.length} upcoming games:</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                    {oddsEvents.map((ev) => (
                      <div
                        key={ev.id}
                        className="px-3 py-2 bg-zinc-800/50 rounded-lg text-sm"
                      >
                        <span className="text-white">{ev.awayTeam} @ {ev.homeTeam}</span>
                        <span className="text-zinc-500 ml-2 text-xs">
                          {new Date(ev.commenceTime).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sync Controls */}
            <div className="flex items-end gap-4 flex-wrap">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">Season</label>
                <input
                  type="number"
                  value={syncSeason}
                  onChange={(e) => setSyncSeason(Number(e.target.value))}
                  className="w-24 px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">Week</label>
                <input
                  type="number"
                  value={syncWeek}
                  onChange={(e) => setSyncWeek(Number(e.target.value))}
                  className="w-24 px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div className="flex items-center pb-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeAlts}
                    onChange={(e) => setIncludeAlts(e.target.checked)}
                    className="w-4 h-4 rounded border-zinc-700 bg-zinc-800 text-blue-500 focus:ring-blue-500"
                  />
                  <span className="text-sm text-zinc-300">Include Alt Lines</span>
                </label>
              </div>
              <Button onClick={syncProps} disabled={syncing}>
                {syncing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4" />
                    Sync Props
                  </>
                )}
              </Button>
            </div>

            {syncResult && (
              <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400 text-sm">
                <CheckCircle className="h-4 w-4 flex-shrink-0" />
                <span>
                  {syncResult.message}
                  {syncResult.usage && (
                    <span className="text-zinc-400 ml-2">
                      (API: {syncResult.usage.requestsUsed} used, {syncResult.usage.remaining} remaining)
                    </span>
                  )}
                </span>
              </div>
            )}

            {syncError && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span>{syncError}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Section 3: View Synced Props ───────────────────────────────────── */}
      <div className="bg-zinc-900 rounded-xl border border-zinc-800">
        <button
          onClick={() => setShowProps(!showProps)}
          className="w-full flex items-center justify-between p-4 text-left"
        >
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-purple-400" />
            <h2 className="text-lg font-semibold text-white">View Synced Props</h2>
            {propsTotal > 0 && (
              <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded text-xs font-medium">
                {propsTotal} total
              </span>
            )}
          </div>
          {showProps ? (
            <ChevronUp className="h-4 w-4 text-zinc-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-zinc-400" />
          )}
        </button>

        {showProps && (
          <div className="px-4 pb-4 space-y-4 border-t border-zinc-800 pt-4">
            {/* Filters */}
            <div className="flex items-end gap-4 flex-wrap">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">Season</label>
                <input
                  type="number"
                  value={propsFilter.season}
                  onChange={(e) => setPropsFilter((f) => ({ ...f, season: Number(e.target.value) }))}
                  className="w-24 px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-purple-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">Week</label>
                <input
                  type="number"
                  value={propsFilter.week}
                  onChange={(e) => setPropsFilter((f) => ({ ...f, week: Number(e.target.value) }))}
                  className="w-24 px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-purple-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">Category</label>
                <select
                  value={propsFilter.category}
                  onChange={(e) => setPropsFilter((f) => ({ ...f, category: e.target.value }))}
                  className="px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-purple-500 focus:outline-none"
                >
                  <option value="">All</option>
                  {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">Player</label>
                <input
                  type="text"
                  value={propsFilter.playerName}
                  onChange={(e) => setPropsFilter((f) => ({ ...f, playerName: e.target.value }))}
                  placeholder="Search..."
                  className="w-40 px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:border-purple-500 focus:outline-none"
                />
              </div>
              <Button onClick={fetchProps} disabled={loadingProps}>
                {loadingProps ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Search className="h-4 w-4" />
                    Load Props
                  </>
                )}
              </Button>
            </div>

            {propsError && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span>{propsError}</span>
              </div>
            )}

            {/* Props grouped by player with alt-line sliders */}
            {props.length === 0 && !loadingProps && (
              <p className="text-zinc-500 text-sm text-center py-8">
                No props found. Sync from the Odds API first, then click "Load Props".
              </p>
            )}

            {Object.entries(groupedProps).map(([playerName, playerProps]) => (
              <PlayerPropSliders
                key={playerName}
                playerName={playerName}
                props={playerProps}
              />
            ))}

            {propsTotal > props.length && (
              <p className="text-xs text-zinc-500 text-center">
                Showing {props.length} of {propsTotal} props. Use filters to narrow results.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
